import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const STORAGE_KEY = "uppit_cookie_consent_v1";
const CONSENT_VALIDITY_DAYS = 180; // re-prompt twice a year

const DEFAULT_CONSENT = {
  necessary: true,
  analytics: false,
  marketing: false,
  timestamp: null,
};

const CookieConsentContext = createContext({
  consent: DEFAULT_CONSENT,
  hasDecided: false,
  open: false,
  setOpen: () => {},
  acceptAll: () => {},
  declineAll: () => {},
  saveCustom: () => {},
  reset: () => {},
});

export const useCookieConsent = () => useContext(CookieConsentContext);

const readStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.timestamp) return null;
    const ageDays = (Date.now() - new Date(parsed.timestamp).getTime()) / 86400000;
    if (ageDays > CONSENT_VALIDITY_DAYS) return null;
    return { ...DEFAULT_CONSENT, ...parsed, necessary: true };
  } catch {
    return null;
  }
};

export const CookieConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(DEFAULT_CONSENT);
  const [hasDecided, setHasDecided] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const stored = readStored();
    if (stored) {
      setConsent(stored);
      setHasDecided(true);
    } else {
      // Auto-open after slight delay so it doesn't compete with first paint
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, []);

  const persist = useCallback((next) => {
    const value = { ...DEFAULT_CONSENT, ...next, necessary: true, timestamp: new Date().toISOString() };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {
      /* ignore */
    }
    setConsent(value);
    setHasDecided(true);
    setOpen(false);
  }, []);

  const acceptAll = useCallback(
    () => persist({ analytics: true, marketing: true }),
    [persist]
  );
  const declineAll = useCallback(
    () => persist({ analytics: false, marketing: false }),
    [persist]
  );
  const saveCustom = useCallback(
    (custom) => persist({ analytics: !!custom.analytics, marketing: !!custom.marketing }),
    [persist]
  );
  const reset = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setConsent(DEFAULT_CONSENT);
    setHasDecided(false);
    setOpen(true);
  }, []);

  const value = useMemo(
    () => ({ consent, hasDecided, open, setOpen, acceptAll, declineAll, saveCustom, reset }),
    [consent, hasDecided, open, acceptAll, declineAll, saveCustom, reset]
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};
